const WEB_PROTOCOLS = new Set(["http:", "https:"]);

// A separator directly followed by another link ends the candidate, so "a,b" becomes two links while a
// link nested in a query string ("?url=https://…") stays whole.
const HTTPS_LINK_CANDIDATE = /https:\/\/(?:(?![,;]https:\/\/)\S)+/gi;
const TRAILING_PUNCTUATION = /[.,;:!?'"*>]$/;
const CLOSING_BRACKETS: Record<string, string> = {
  ")": "(",
  "]": "[",
  "}": "{",
};

/** A run of free text, carrying `href` when the run is a link that may be opened. */
export interface TextSegment {
  text: string;
  href: string | null;
}

/**
 * Whether a stored link may go into an `href`: an absolute http or https URL.
 *
 * SECURITY: a `javascript:` or `data:` link runs in the viewer's session when clicked, and a relative
 * one resolves against this site, so callers render anything else as plain text.
 */
export const isSafeUrl = (url: string): boolean => {
  try {
    return WEB_PROTOCOLS.has(new URL(url).protocol);
  } catch {
    return false;
  }
};

/**
 * The entries of a comma-separated link field that may be opened, in order; free text such as a
 * contract name or a placeholder is skipped rather than opened as a path on this site.
 *
 * SECURITY: forwards deal owners write these fields, and they reach the admin transaction form.
 */
export const openableLinks = (value: string | null): string[] =>
  (value ?? "")
    .split(",")
    .map((entry) => entry.trim())
    .filter(isSafeUrl);

/**
 * Splits free text into plain runs and the https links inside it, so a template can render the links
 * as anchors without `v-html`. Best effort: punctuation ending a sentence, and a closing bracket the
 * link never opened, stay outside the link.
 *
 * SECURITY: only runs that pass `isSafeUrl` carry an `href`.
 */
export const textWithLinks = (text: string): TextSegment[] => {
  const segments: TextSegment[] = [];
  let plainStart = 0;
  for (const match of text.matchAll(HTTPS_LINK_CANDIDATE)) {
    const link = withoutTrailingPunctuation(match[0]);
    if (!isSafeUrl(link)) continue;
    pushPlain(segments, text.slice(plainStart, match.index));
    segments.push({ text: link, href: link });
    plainStart = match.index + link.length;
  }
  pushPlain(segments, text.slice(plainStart));
  return segments;
};

const withoutTrailingPunctuation = (candidate: string): string => {
  let link = candidate;
  while (link.length > 0) {
    const last = link[link.length - 1];
    const opening = CLOSING_BRACKETS[last];
    const unbalanced =
      opening !== undefined && count(link, opening) < count(link, last);
    if (!TRAILING_PUNCTUATION.test(last) && !unbalanced) break;
    link = link.slice(0, -1);
  }
  return link;
};

const count = (text: string, char: string): number =>
  text.split(char).length - 1;

const pushPlain = (segments: TextSegment[], text: string) => {
  if (text.length > 0) segments.push({ text, href: null });
};
